import React, { useState, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import CustomCard from "../../molecules/ChallengeCreator/CustomCard";
import { useTheme } from "styled-components";
import HeroUploader from "../../molecules/ChallengeCreator/HeroUploader";
import ChallengeCard from "../../molecules/ChallengeCard";
import { deleteChallengeImageFromFirebase } from "../../../services/firebaseStorage"; // Import the deletion function

const ChallengeCoverForm = ({
  onImageUpload,
  formData,
  userData,
  setFormData,
  userId,
  disabled, // New prop to determine if the form is disabled
}) => {
  const theme = useTheme();

  // Initialize with formData.hero if available
  const [imageUrl, setImageUrl] = useState(formData.hero || null);
  const [isReplacing, setIsReplacing] = useState(false); // Flag to toggle between showing ChallengeCard and uploader
  const [previousImageUrl, setPreviousImageUrl] = useState(null); // Holds the previous image URL for reverting

  // Sync imageUrl with formData.hero when component mounts or formData changes
  useEffect(() => {
    if (formData.hero) {
      setImageUrl(formData.hero);
      setPreviousImageUrl(formData.hero);
    } else {
      // Clear the imageUrl if formData.hero is null
      setImageUrl(null);
      setPreviousImageUrl(null);
    }
  }, [formData.hero]);

  // Handle image upload and save previous image before replacing
  const handleImageUpload = async (uploadedUrl) => {
    console.log("New image uploaded:", uploadedUrl);
    // Delete the previous image if one exists and the new image is different
    if (previousImageUrl && uploadedUrl !== previousImageUrl) {
      await deleteChallengeImageFromFirebase(previousImageUrl);
      console.log("Deleted previous image:", previousImageUrl);
    }

    setImageUrl(uploadedUrl); // Update the state with the uploaded image URL
    onImageUpload(uploadedUrl); // Call the parent’s function to send the image URL back

    // Update formData manually without using handleFormChange
    setFormData((prev) => ({
      ...prev,
      hero: uploadedUrl,
    })); // Update the formData state in parent
    setIsReplacing(false); // Close uploader after new image upload
    setPreviousImageUrl(uploadedUrl); // Store the new image URL for future deletion
  };

  // Show the uploader for replacing the image
  const handleReplaceImage = () => {
    console.log("Replace image initiated");
    setPreviousImageUrl(imageUrl); // Save the current image URL before replacing
    setIsReplacing(true); // Show uploader to allow the user to pick a new image
  };

  // Handle the cancel action and revert to the previous image
  const handleCancelReplace = () => {
    console.log("Cancel replace initiated");
    setIsReplacing(false); // Cancel the replacement, return to showing ChallengeCard
    setImageUrl(previousImageUrl); // Restore the previously uploaded image URL
  };

  return (
    <CustomCard title="Upload a cover for your Challenge">
      <Box
        sx={{
          backgroundColor:
            isReplacing || !imageUrl ? theme.colors.green : "transparent", // Green when replacing or uploading, transparent when card is shown
          borderRadius: "8px",
          textAlign: "center",
          color: "white",
          padding: isReplacing ? 2 : 4,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center", // Center the content vertically
          alignItems: "center", // Center the content horizontally
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Show ChallengeCard as soon as image is uploaded */}
        {imageUrl && !isReplacing ? (
          <>
            {/* Conditionally hide the "Replace Image" button if disabled */}
            {!disabled && (
              <Button
                variant="contained"
                onClick={handleReplaceImage}
                sx={{
                  backgroundColor: theme.colors.blue,
                  fontFamily: "basic-sans",
                  fontWeight: "bold",
                  color: theme.colors.white,
                  textTransform: "none",
                  borderRadius: "50px",
                  padding: "5px 20px",
                  boxShadow: "none",
                  mb: 8,
                  "&:hover": {
                    backgroundColor: theme.colors.red,
                    boxShadow: "none",
                  },
                }}
              >
                Replace Image
              </Button>
            )}
            <ChallengeCard
              challenge={{
                title: formData.title,
                prize: formData.prize, // Even if prize is missing, it'll show "TBD" in the card
                hero: imageUrl,
                id: null, // Replace with actual ID if available
                creatorInfo: {
                  profileImage: userData.profileImage,
                  userName: userData.userName,
                  displayName: userData.displayName,
                },
              }}
            />
          </>
        ) : (
          // Show image uploader only when replacing or no image is uploaded yet
          <>
            <Typography
              variant="body1"
              sx={{ fontFamily: "basic-sans", mb: 2, fontSize: "1.2em" }}
            >
              Choose an image no larger than 20MB
            </Typography>

            {/* ImageUploader component */}
            <HeroUploader
              onImageUpload={handleImageUpload}
              userId={userId}
              onCancel={handleCancelReplace} // Pass the cancel function
              disabled={disabled} // Pass the disabled prop
            />
            {/* Cancel button to revert back */}
            {isReplacing && (
              <Button
                variant="outlined"
                onClick={handleCancelReplace}
                sx={{
                  fontFamily: "basic-sans",
                  color: theme.colors.white,
                  borderColor: theme.colors.white,
                  borderRadius: "50px",
                  mt: 2,
                  "&:hover": {
                    borderColor: theme.colors.darkPurple,
                  },
                }}
              >
                Cancel
              </Button>
            )}
          </>
        )}
      </Box>
    </CustomCard>
  );
};

export default ChallengeCoverForm;
